<script setup>
import { ref, nextTick, onMounted, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, EffectCoverflow } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/effect-coverflow'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import * as fancyboxPkg from '@fancyapps/ui'
const { Fancybox } = fancyboxPkg

import { openPopup } from '@/components/popup/usePopup'

import { useRequestsStore } from '@/stores/storeRequests'
const storeRequests = useRequestsStore()

// Состояние
const items = ref([])
const filteredItems = ref([])
const realIndex = ref(0)
const isLoading = ref(false)
const filterPhoto = ref(true)
const filterVideo = ref(true)
const textBodyRefs = ref([])
const showButton = ref([]) // массив флагов для каждого отзыва

const videoRefs = ref([]) // refs для видео
const videoPlaying = ref([]) // состояние видео

// Нормализация данных из API
const normalizeData = (data) => {
	return data.map((item) => ({
		id: item.id,
		img: item.image,
		video: item.video,
		text: item.text,
		rating: item.rating,
	}))
}

// Получение отзывов
const fetchReviews = async () => {
	isLoading.value = true
	try {
		const data = await storeRequests.getReviews()
		const normalized = normalizeData(data)
		// дублируем, чтобы было в 2 раза больше
		items.value = [...normalized, ...normalized]
		applyFilter()
		await nextTick()
	} catch (err) {
		console.error('Ошибка при загрузке отзывов', err)
	} finally {
		isLoading.value = false
	}
}

// Фильтрация
const applyFilter = () => {
	filteredItems.value = items.value.filter((item) => {
		// Если фильтр "с фото" выключен, то не показываем отзывы только с фото
		if (!filterPhoto.value && item.img && !item.video) return false

		// Если фильтр "с видео" выключен, то не показываем отзывы с видео
		if (!filterVideo.value && item.video) return false

		return true
	})
	realIndex.value = 0
}

// Проверка переполнения текста
const checkTextOverflow = async () => {
	await nextTick()
	showButton.value = filteredItems.value.map((_, index) => {
		const el = textBodyRefs.value[index]
		if (!el) return false
		return el.scrollHeight > el.clientHeight
	})
}

// Обработка смены слайда
const handleSlideChange = (swiper) => {
	realIndex.value = swiper.realIndex
}

// Инициализация
onMounted(async () => {
	await fetchReviews()
	await nextTick()
	checkTextOverflow()

	Fancybox.bind('[data-fancybox]', {
		Hash: false,
		plugins: [],
		Carousel: {
			Navigation: false,
			Toolbar: {
				display: {
					left: [''],
					right: ['close'],
				},
			},
		},
	})
})

// После фильтрации или загрузки отзывов
watch(filteredItems, async () => {
	videoPlaying.value = filteredItems.value.map(() => false)
	await nextTick()
	checkTextOverflow()
})
</script>

<template>
	<section class="reviews" id="reviews">
		<div class="reviews__container">
			<div class="reviews__title">
				<h2><span>Отзывы</span> покупателей</h2>
				<div class="reviews__filter">
					<label class="checkbox__item">
						<input
							type="checkbox"
							class="checkbox__input"
							v-model="filterPhoto"
							@change="applyFilter"
						/>
						<span class="checkbox__emulator"></span>
						<span class="checkbox__label">С фото</span>
					</label>
					<label class="checkbox__item">
						<input
							type="checkbox"
							class="checkbox__input"
							v-model="filterVideo"
							@change="applyFilter"
						/>
						<span class="checkbox__emulator"></span>
						<span class="checkbox__label">С видео</span>
					</label>
				</div>
			</div>

			<div class="reviews__block">
				<div v-if="isLoading" class="reviews__loader">Загрузка...</div>

				<div class="reviews__wrapper" v-else-if="filteredItems.length">
					<Swiper
						class="reviews__slider"
						:modules="[Navigation, EffectCoverflow]"
						effect="coverflow"
						:grabCursor=true
						:slides-per-view="'auto'"
						:space-between="0"
						:navigation="{
							prevEl: '.reviews__button-prev',
							nextEl: '.reviews__button-next',
						}"
						:centeredSlides="true"
						:coverflowEffect="{
							rotate: 0,
							stretch: 180,
							depth: 120,
							modifier: 1,
							slideShadows: false,
						}"
						:initialSlide="Math.floor(items.length / 2)"
						:loop="true"
						:breakpoints="{
							0: {
								coverflowEffect: {
									rotate: 0,
									stretch: 100,
									depth: 100,
									modifier: 1,
									slideShadows: false,
								},
							},
							991.98: {
								coverflowEffect: {
									rotate: 0,
									stretch: 180,
									depth: 120,
									modifier: 1,
									slideShadows: false,
								},
							},
						}"
						@slideChange="handleSlideChange"
					>
						<SwiperSlide
							v-for="(item, index) in filteredItems"
							:key="item.id"
							class="reviews__slide"
						>
							<div class="reviews__item">
								<div class="reviews__media">
									<a
										v-if="item.video"
										:href="item.video"
										:data-fancybox="`galleryReviews${index}`"
									>
										<video
											:src="item.video"
											:poster="item.img"
											ref="el => { if(el) videoRefs.value[item.id] = el }"
											preload="metadata"
										></video>
										<div class="reviews__play-icon">
											<svg width="44" height="44">
												<use :href="`#play`" />
											</svg>
										</div>
									</a>

									<img v-else-if="item.img" :src="item.img" :alt="item.text" />
								</div>
								<div class="reviews__desc">
									<div class="reviews__head">
										<div class="reviews__rating">
											<ul>
												<li v-for="n in item.rating" :key="n">
													<svg><use href="#star-review" /></svg>
												</li>
											</ul>
										</div>
									</div>
									<div class="reviews__text">
										<div
											class="reviews__text_body"
											:ref="(el) => (textBodyRefs[index] = el)"
										>
											{{ item.text }}
										</div>
										<button
											v-if="showButton[index]"
											type="button"
											class="reviews__text_btn"
											@click="
												openPopup('aboutReviews', null, { review: item })
											"
										>
											Посмотреть полностью
										</button>
									</div>
								</div>
							</div>
						</SwiperSlide>
					</Swiper>

					<div class="reviews__actions">
						<button type="button" class="arrow-btn reviews__button-prev">
							<svg>
								<use :href="`#arrow-left`" />
							</svg>
						</button>
						<button type="button" class="arrow-btn reviews__button-next">
							<svg>
								<use :href="`#arrow-right`" />
							</svg>
						</button>
					</div>
				</div>

				<h3 v-if="!isLoading && filteredItems.length === 0">Отзывов не найдено.</h3>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.reviews {
	padding: 8.4rem 0 29.8rem;

	@media (max-width: 991.98px) {
		padding: 3.6rem 0 23.5rem;
	}

	// .reviews__container
	&__container {
	}

	// .reviews__title
	&__title {
		text-align: center;
	}

	// .reviews__filter
	&__filter {
		margin-top: 3.5rem;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4.4rem;

		@media (max-width: 991.98px) {
			margin-top: 2.8rem;
			gap: 2.5rem;
		}
	}

	&__wrapper {
		position: relative;
	}

	// .reviews__block
	&__block {
		margin-top: 6rem;

		@media (max-width: 991.98px) {
			margin-top: 2.7rem;
		}

		h3 {
			text-align: center;
		}
	}

	// .reviews__loader
	&__loader {
	}

	// .reviews__slider
	&__slider {
		overflow: visible;
	}

	// .reviews__slide
	&__slide {
		max-width: 40.4rem;
		width: 100%;
		height: 47.6rem;
		position: relative;

		@media (max-width: 991.98px) {
			max-width: 22.5rem;
			height: 26.6rem;
		}

		&.swiper-slide-active {
			.reviews__media::before {
				opacity: 0;
				visibility: hidden;
			}
		}
	}

	// .reviews__item
	&__item {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		position: relative;
	}

	// .reviews__media
	&__media {
		height: 100%;
		width: 100%;
		border: 1px solid rgba(255, 255, 255, 0.32);
		border-radius: 3rem;
		overflow: hidden;
		position: relative;

		&:before {
			content: '';
			position: absolute;
			width: 100%;
			height: 100%;
			top: 0;
			left: 0;
			border-radius: inherit;
			background-color: rgba(0, 0, 0, 0.5);
			transition: all 0.3s ease 0s;
		}

		@media (max-width: 991.98px) {
			border-radius: 1.6rem;
		}

		img,
		video,
		iframe {
			width: 100%;
			height: 100%;
		}

		img,
		video {
			object-fit: cover;
		}

		.reviews__play-icon {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			cursor: pointer;
			z-index: 2;
			flex-shrink: 0;
			width: 10rem;
			height: 10rem;
			border-radius: 50%;
			background-color: var(--primary-color);
			display: flex;
			align-items: center;
			justify-content: center;
			transition: background-color 0.3s ease 0s;

			svg {
				width: 4.4rem;
				height: 4.4rem;
			}

			@media (max-width: 991.98px) {
				width: 5rem;
				height: 5rem;

				svg {
					width: 2.2rem;
					height: 2.2rem;
				}
			}

			&::after {
				content: '';
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				border-radius: inherit;
				width: 100%;
				height: 100%;
				border: 1px solid var(--primary-color);
				animation: pulse 2s linear infinite both;
				pointer-events: none;
				transition: border-color 0.3s ease 0s;

				@keyframes pulse {
					0% {
						transform: translate(-50%, -50%) scale(1);
						opacity: 1;
					}
					100% {
						transform: translate(-50%, -50%) scale(1.3);
						opacity: 0;
					}
				}
			}
		}
	}

	// .reviews__desc
	&__desc {
		font-family: var(--second-font);
		position: absolute;
		width: 88.2rem;
		border: 1px solid rgba(255, 255, 255, 0.24);
		border-radius: 2rem;
		padding: 3.6rem 4rem 4.4rem;
		top: calc(100% + 6rem);
		background: #323232;
		opacity: 0;
		visibility: hidden;
		transition: all 0.3s ease 0s;

		@media (max-width: 991.98px) {
			top: calc(100% + 2.7rem);
			width: 33rem;
			padding: 1.4rem 1.2rem;
		}

		.swiper-slide-active & {
			opacity: 1;
			visibility: visible;
		}
	}

	&__head {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1.8rem;
	}

	// .reviews__rating
	&__rating {
		ul {
			display: flex;
			align-items: center;

			li {
				svg {
					width: 2.4rem;
					height: 2.4rem;

					@media (max-width: 991.98px) {
						width: 1.8rem;
						height: 1.8rem;
					}
				}
			}
		}
	}

	// .reviews__text
	&__text {
		font-weight: 600;
		font-size: 2.4rem;
		// line-height: 2.8rem;
		line-height: 116%;
		text-align: center;
		color: #fff;
		margin-top: 1.8rem;

		@media (max-width: 991.98px) {
			font-size: 1.6rem;
			margin-top: 1.4rem;
		}

		&_body {
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}

		&_btn {
			margin-top: 1rem;
			color: var(--primary-color);
		}
	}

	// .reviews__actions
	&__actions {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: space-between;
		pointer-events: none;
		user-select: none;
		width: 100%;
		max-width: 106.4rem;
		left: 50%;
		transform: translateX(-50%);
		top: calc(100% + 12.5rem);
		z-index: 100;

		@media (max-width: 991.98px) {
			top: 10.5rem;
		}
	}

	// .moore-looks__button-prev
	&__button-prev,
	&__button-next {
		pointer-events: all;
		color: #fff;

		svg {
			width: 6.2rem;
			height: 4.8rem;

			@media (max-width: 991.98px) {
				width: 4.4rem;
				height: 5rem;
			}
		}
	}
}

.checkbox {
	&__item {
		display: inline-flex;
		align-items: center;
		cursor: pointer;
		position: relative;
		gap: 1.3rem;
		padding: 0;
		font-weight: 400;
		font-size: 3.6rem;
		// line-height: 4.2rem;
		line-height: 116%;
		text-transform: lowercase;
		text-align: center;

		@media (max-width: 991.98px) {
			font-size: 2.2rem;
		}
	}

	&__emulator {
		display: block;
		height: 4rem;
		flex: 0 0 4rem;
		position: relative;
		border: 1px solid rgba(255, 255, 255, 0.4);
		border-radius: 0.4rem;
		transition: all 0.3s ease 0s;
		position: relative;
		margin: 0;
		outline: none;

		@media (max-width: 991.98px) {
			height: 3rem;
			flex: 0 0 3rem;
		}

		&::before {
			content: '';
			position: absolute;
			width: 100%;
			height: 100%;
			top: 50%;
			left: 50%;
			opacity: 0;
			z-index: 1;
			transform: translate(-50%, -50%) scale(1.5);
			transition: ease-out 0.15s;
			background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='42' height='40' viewBox='0 0 42 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M16.1946 29C16.1946 28.7974 16.1557 28.6961 16.0777 28.6961L15.5399 28.9532C15.5399 28.8442 15.4775 28.7662 15.3528 28.7195L15.1657 28.6961C15.041 28.6961 14.8851 28.7507 14.6981 28.8597C14.6669 28.7818 14.6279 28.7039 14.5812 28.626C14.5344 28.5481 14.4954 28.4779 14.4642 28.4156C14.2616 28.026 14.0589 27.5974 13.8563 27.1299C13.6692 26.6468 13.4899 26.187 13.3184 25.7506C13.1625 25.3143 13.0378 24.9714 12.9443 24.7221C12.8819 24.5195 12.8118 24.2156 12.7338 23.8104C12.6559 23.4052 12.5779 22.8909 12.5 22.2675C12.6715 22.3766 12.804 22.4312 12.8975 22.4312C13.0066 22.4312 13.108 22.2675 13.2015 21.9403C13.2483 22.0026 13.334 22.0338 13.4587 22.0338C13.5523 22.0338 13.6224 22.0026 13.6692 21.9403L14.0433 21.3792L14.4642 21.5195H14.4876C14.5188 21.5195 14.55 21.5039 14.5812 21.4727C14.6123 21.4416 14.6591 21.4104 14.7215 21.3792C14.8462 21.3013 14.9397 21.2623 15.0021 21.2623L15.0722 21.2857C15.4619 21.4727 15.7114 21.8156 15.8205 22.3143C16.1011 23.4987 16.3817 24.0909 16.6623 24.0909C16.9429 24.0909 17.2703 23.7948 17.6444 23.2026C17.8315 22.9065 18.0186 22.5636 18.2056 22.174C18.4083 21.7844 18.611 21.3481 18.8136 20.8649C18.8448 21.0519 18.876 21.1455 18.9072 21.1455C18.9851 21.1455 19.1176 20.9506 19.3047 20.561C19.5073 20.1714 19.8269 19.6338 20.2634 18.9481C20.5128 18.5273 20.8246 18.0519 21.1988 17.5221C21.5885 16.9922 22.0016 16.4468 22.4381 15.8857C22.8746 15.3247 23.2955 14.7948 23.7008 14.2961C24.1217 13.7974 24.4959 13.3688 24.8232 13.0104C25.1506 12.6519 25.3923 12.4182 25.5481 12.3091C26.1405 11.9039 26.6082 11.5143 26.9512 11.1403C26.9356 11.2493 26.9122 11.3506 26.881 11.4442C26.8654 11.5221 26.8576 11.5766 26.8576 11.6078C26.8576 11.6701 26.8888 11.7013 26.9512 11.7013L27.6059 11.374V11.4675C27.6059 11.5922 27.6371 11.6545 27.6994 11.6545C27.7462 11.6545 27.8398 11.5844 27.9801 11.4442C28.1204 11.3039 28.1983 11.2026 28.2139 11.1403L28.1671 11.4675L28.9622 11L28.7751 11.4208C29.0245 11.2494 29.2038 11.1636 29.3129 11.1636C29.3753 11.1636 29.4221 11.2026 29.4532 11.2805C29.4844 11.3429 29.5 11.4052 29.5 11.4675C29.5 11.561 29.461 11.6701 29.3831 11.7948C29.3051 11.9195 29.2038 12.0675 29.0791 12.239C28.9856 12.3636 28.8297 12.5506 28.6114 12.8C28.4088 13.0338 28.097 13.3844 27.6761 13.8519C27.2552 14.3039 26.6939 14.9351 25.9924 15.7455C25.8054 15.9481 25.517 16.3065 25.1272 16.8208C24.7375 17.3195 24.2932 17.9039 23.7944 18.574C23.3111 19.2286 22.8278 19.8909 22.3446 20.561C21.8613 21.2312 21.4326 21.839 21.0585 22.3844C20.6843 22.9143 20.4193 23.3117 20.2634 23.5766L18.8136 26.0312C18.5018 26.561 18.2446 26.9974 18.042 27.3403C17.8393 27.6675 17.6834 27.8935 17.5743 28.0182C17.3404 28.2987 17.0832 28.5481 16.8026 28.7662L16.5922 28.6494L16.4051 28.7662L16.1946 29Z' fill='white'/%3e%3c/svg%3e ");
			background-repeat: no-repeat;
			background-position: center;
			background-size: contain;
		}
	}

	&__input {
		position: absolute;
		z-index: 10;
		opacity: 0;
		visibility: hidden;
		font-size: 0px;
		width: 100%;
		height: 100%;

		&:checked ~ .checkbox__emulator {
			border-color: #fff;
		}

		&:checked ~ .checkbox__emulator::before {
			opacity: 1;
			transform: translate(-50%, -50%) scale(1);
		}
	}
}
</style>
